import * as React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Radio,
  Stack,
  CircularProgress,
  Alert,
  useTheme
} from '@mui/material';
import {
  StarRounded as ProIcon,
  DiamondRounded as UltraIcon,
  CheckCircleRounded as FreeIcon
} from '@mui/icons-material';
import ApiClient from '../../utils/apiClient';

interface Product {
  product_id: string;
  price_id: string;
  name: string;
  full_name: string;
  access_level: string;
  cost: number;
  renewal_cycle: string;
  renewal_cycle_id: string;
}

interface ProductSelectionProps {
  onContinue?: (selectedProduct: Product) => void;
  onNavigate?: (page: string, data?: any) => void;
}

export default function ProductSelection({ onContinue, onNavigate }: ProductSelectionProps) {
  const theme = useTheme();
  const [products, setProducts] = React.useState<Product[]>([]);
  const [selectedPriceId, setSelectedPriceId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // Payment state moved here so hooks are at component scope
  const [processingPayment, setProcessingPayment] = React.useState(false);
  const [paymentError, setPaymentError] = React.useState<string | null>(null);

  // Currently selected product (derived)
  const selectedProduct = products.find(p => p.price_id === selectedPriceId) || null;

  const handlePaymentSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setPaymentError(null);

    if (!selectedProduct) {
      setPaymentError('Please select a plan');
      return;
    }

    try {
      setProcessingPayment(true);
      const { checkout_url } = await ApiClient.post('api/payments/create-checkout-session', {
        price_id: selectedProduct.price_id
      });

      window.location.href = checkout_url;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setPaymentError(errorMessage);
      setProcessingPayment(false);
    }
  };

  // Group products by product_id
  const groupedProducts = React.useMemo(() => {
    const groups: { [key: string]: Product[] } = {};
    products.forEach(product => {
      if (!groups[product.product_id]) {
        groups[product.product_id] = [];
      }
      groups[product.product_id].push(product);
    });
        
    return groups;
  }, [products]);

  // Fetch products from API
  React.useEffect(() => {
    const fetchProducts = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('Authentication required');
          return;
        }

        const data = await ApiClient.get('api/payments/products');
        if (data.success) {
          setProducts(data.products);
        } else {
          setError(data.msg || 'Failed to fetch products');
        }
      } catch (err) {
        setError('Network error occurred');
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // When products are loaded, select the first price of the first product by default
  React.useEffect(() => {
    if (products.length > 0 && !selectedPriceId) {
      // products array typically contains each price as a separate entry; choose the first item's price_id
      setSelectedPriceId(products[0].price_id);
    }
  }, [products, selectedPriceId]);

  const getProductIcon = (accessLevel: string) => {
    switch (accessLevel) {
      case 'pro':
        return <ProIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;
      case 'ultra':
        return <UltraIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />;
      default:
        return <FreeIcon sx={{ fontSize: 40, color: theme.palette.text.secondary }} />;
    }
  };

  const getProductColor = (accessLevel: string) => {
    switch (accessLevel) {
      case 'pro':
        return theme.palette.primary.main;
      case 'ultra':
        return theme.palette.primary.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const formatPrice = (cost: number, renewalCycle: string) => {
    if (cost === 0) return 'Free';
    return `£${cost.toFixed(2)} / ${renewalCycle.toLowerCase()}`;
  };

  const handleCardClick = (product: Product) => {
    setSelectedPriceId(product.price_id);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography component="h1" variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
        Upgrade to Pro
      </Typography>
      
      

      <Stack spacing={3} sx={{ maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Start your 7-day free trial of our Pro subscription to support the running and development of EnergyInfo.uk and gain access to advanced data visualisations and 30-minute energy consumption data. 
        </Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Cancel anytime before your trial ends and you won't be charged. If you don't cancel before the trial expires, you'll automatically roll onto a paid subscription and be billed according to your selected plan.
        </Typography>
        {Object.entries(groupedProducts).map(([productId, productGroup]) => {
          const firstProduct = productGroup[0];
          const isProductSelected = productGroup.some(p => selectedPriceId === p.price_id);
          
          return (
            <Card
              key={`product-${productId}`}
              sx={{
                border: isProductSelected 
                  ? `2px solid ${getProductColor(firstProduct.access_level)}` 
                  : '2px solid transparent',
                backgroundColor: isProductSelected 
                  ? theme.palette.mode === 'dark' 
                    ? theme.palette.action.selected
                    : theme.palette.action.hover
                  : theme.palette.background.paper,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  border: `2px solid ${getProductColor(firstProduct.access_level)}`,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? theme.palette.action.hover
                    : theme.palette.action.hover,
                  boxShadow: theme.shadows[4]
                }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={3}>
                  {/* Product Header */}
                  <Stack direction="row" spacing={3} alignItems="flex-start">
                    {/* Product Icon */}
                    <Box sx={{ flexShrink: 0 }}>
                      {getProductIcon(firstProduct.access_level)}
                    </Box>

                    {/* Product Details */}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" component="h2" sx={{ mb: 1, fontWeight: 'bold' }}>
                        {firstProduct.name.replace(/ (Monthly|Annually)$/, '')}
                      </Typography>
                      
                      <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                        {firstProduct.full_name}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Pricing Options */}
                  <Stack spacing={2}>
                    {productGroup.map((product) => (
                      <Box
                        key={`price-${product.price_id}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 2,
                          border: selectedPriceId === product.price_id 
                            ? `1px solid ${getProductColor(product.access_level)}`
                            : `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          backgroundColor: selectedPriceId === product.price_id 
                            ? theme.palette.mode === 'dark' 
                              ? theme.palette.action.selected
                              : theme.palette.action.hover
                            : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                            border: `1px solid ${getProductColor(product.access_level)}`
                          }
                        }}
                        onClick={() => handleCardClick(product)}
                      >
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            Free for 7 days then {formatPrice(product.cost, product.renewal_cycle_id)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Billed {product.renewal_cycle.toLowerCase()}
                          </Typography>
                        </Box>
                        
                        <Radio
                          checked={selectedPriceId === product.price_id}
                          onChange={() => handleCardClick(product)}
                          sx={{
                            color: getProductColor(product.access_level),
                            '&.Mui-checked': {
                              color: getProductColor(product.access_level)
                            }
                          }}
                        />
                      </Box>
                    ))}
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          );
        })}
        <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary' }}>
          Complete Payment
        </Typography>
        <Card sx={{ mt: 4, backgroundColor: theme.palette.background.paper }}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3} sx={{ mx: 'auto' }}>
              {/* Payment Form */}
              <Card sx={{ backgroundColor: theme.palette.background.paper }}>
                <CardContent sx={{ p: 3 }}>  
                  {paymentError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                          {paymentError}
                        </Alert>
                      )}

                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    You will be redirected to our payment partner, Stripe, to complete your purchase.
                  </Typography>

                  <form onSubmit={(e) => { e.preventDefault(); handlePaymentSubmit(); }}>
                    <Stack spacing={3}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={!selectedProduct || processingPayment}
                        sx={{
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {processingPayment ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Redirecting to Payment...
                          </>
                        ) : selectedProduct ? (
                          `Free for 7 days then pay ${selectedProduct.cost === 0 ? 'Free' : `£${selectedProduct.cost.toFixed(2)} / ${selectedProduct.renewal_cycle_id}`}`
                        ) : (
                          'Continue'
                        )}
                      </Button>
                    </Stack>
                  </form>
                </CardContent>
              </Card>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
